import { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  FormControl,
  FormLabel,
  Tooltip,
} from '@chakra-ui/react';
import { useMediaQuery } from '@chakra-ui/media-query';
import { Field, reduxForm, formValueSelector, reset } from 'redux-form';
import { connect } from 'react-redux';
import countryCodes from 'country-codes-list';
import { gql, useMutation } from '@apollo/client';

const reasons = [
  'Book a demo',
  'Request briefing bundles',
  'Need some help',
  'Provide feedback',
  'Partner inquiry',
  'Integrate with learning platform',
  'Other',
];

const jobRoles = [
  'Partnership Manager',
  'Product Manager',
  'Project Manager',
  'Human resources leader',
  'Learning leader',
  'Content specialist',
  'Technology/ Platform Specialist',
  'Other',
];

const products = [
  'Web Curation',
  'Content to Skills Mapper',
  // 'Complete Curation',
  'All products',
  `I'm not sure`,
];

const hearings = [
  {
    name: 'Recommendation / Referral',
    requirefield: true,
  },
  {
    name: 'Social - LinkedIn',
    requirefield: false,
  },
  {
    name: 'Social - Twitter',
    requirefield: false,
  },
  {
    name: 'Search engine',
    requirefield: false,
  },
  {
    name: 'Blog',
    requirefield: false,
  },
  {
    name: 'Newsletter',
    requirefield: false,
  },
  {
    name: 'Email',
    requirefield: false,
  },
  {
    name: 'Learning Technologies event',
    requirefield: false,
  },
  {
    name: 'Webinar',
    requirefield: false,
  },
  {
    name: 'Other event',
    requirefield: true,
  },
  {
    name: 'Other',
    requirefield: true,
  },
];

const sizes = [
  'Less than 1,000',
  '1,000 - 5,000',
  '5,000- 7,500',
  '7,500 - 10,000',
  '10,000 - 20,000',
  '20,000 - 50,000',
  '50,000 - 100,000',
  '100,000 - 150,000',
  '150,000+',
];

const SEND_CONTACT_MESSAGE = gql`
  mutation (
    $firstName: String!
    $lastName: String!
    $email: String!
    $prefix: String!
    $phone: String!
    $company: String!
    $jobRole: String!
    $country: String!
    $productRequired: String!
    $hearing: String!
    $hearingOption: String
    $message: String!
    $size: String!
    $receiveNewsletters: Boolean!
  ) {
    sendContactMessageNew(
      firstName: $firstName
      lastName: $lastName
      email: $email
      prefix: $prefix
      phone: $phone
      company: $company
      jobRole: $jobRole
      country: $country
      productRequired: $productRequired
      hearing: $hearing
      hearingOption: $hearingOption
      message: $message
      size: $size
      receiveNewsletters: $receiveNewsletters
    ) {
      message
    }
  }
`;

let ContactForm = ({ selectedType, submitting, handleSubmit, dispatch }) => {
  const [sending, setSending] = useState(false);
  const [type, setType] = useState('full');
  const [requireField, setRequieField] = useState(true);

  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)');

  const [sendContactMessageNew, { data, loading, error }] =
    useMutation(SEND_CONTACT_MESSAGE);

  useEffect(() => {
    if (selectedType) {
      setType(selectedType);
    }
    if (loading) {
      setSending(loading);
    } else {
      setTimeout(() => {
        setSending(false);
      }, 2000);
    }

    return () => null;
  }, [selectedType, loading]);

  const onSubmitHandler = (values) => {
    let prefix = values.prefix.split(' ').map((p) => p.trim());

    let message = values.message;
    let productRequired = values.productRequired;
    if (type === 'completeCuration') {
      message = '[User filled in Complete Curation contact form]';
      productRequired = 'Complete Curation';
    }

    sendContactMessageNew({
      variables: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        prefix: prefix[prefix.length - 1],
        phone: values.phone,
        company: values.company,
        jobRole: values.jobRole,
        country: values.country,
        productRequired: productRequired,
        hearing: values.hearing,
        hearingOption: values.hearingOption,
        message: message,
        size: values.size,
        receiveNewsletters: values.receiveNewsletters,
      },
    });

    // Reset the form after submitting
    dispatch(reset('contactForm'));
  };

  if (type && !reasons.includes(type)) {
    reasons.push(type);
  }

  let countries = countryCodes.all();
  countries.sort((a, b) =>
    a.countryNameEn.toLowerCase() < b.countryNameEn.toLowerCase()
      ? -1
      : a.countryNameEn.toLowerCase() > b.countryNameEn.toLowerCase()
      ? 1
      : 0
  );

  return (
    <Flex maxW={720} margin={isLargerThan1024 ? '4rem auto' : '4rem 2rem'}>
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <Flex alignItems="center">
          <FormControl>
            <FormLabel my={2}>First Name *</FormLabel>
            <Field
              name="firstName"
              component="input"
              type="text"
              required
              style={{ outline: 'transparent' }}
              className="customInput"
            />
          </FormControl>
          <FormControl ml={3}>
            <FormLabel my={2}>Last Name *</FormLabel>
            <Field
              name="lastName"
              component="input"
              type="text"
              required
              style={{ outline: 'transparent' }}
              className="customInput"
            />
          </FormControl>
        </Flex>
        {type === 'full' && (
          <Flex justifyItems="center" className="form-group">
            <FormControl>
              <FormLabel my={2}>Prefix</FormLabel>
              <Field
                name="prefix"
                component="select"
                type="number"
                className="customInput"
              >
                {countries.map((country, index) => (
                  <option
                    key={index}
                    value={`${country.countryNameEn} +${country.countryCallingCode}`}
                  >
                    {country.flag} {country.countryNameEn} +
                    {country.countryCallingCode}
                  </option>
                ))}
              </Field>
            </FormControl>
            <FormControl ml={3}>
              <FormLabel my={2}>Phone Number</FormLabel>
              <Field
                name="phone"
                component="input"
                type="number"
                style={{ outline: 'transparent' }}
                className="customInput"
              />
            </FormControl>
          </Flex>
        )}
        <Flex justifyItems="center">
          <FormControl>
            <FormLabel my={2}>Company Name *</FormLabel>
            <Field
              name="company"
              component="input"
              type="text"
              required
              style={{ outline: 'transparent' }}
              className="customInput"
            />
          </FormControl>
          <FormControl ml={3}>
            <FormLabel my={2}>Company Email *</FormLabel>
            <Field
              name="email"
              component="input"
              type="email"
              required
              style={{ outline: 'transparent' }}
              className="customInput"
            />
          </FormControl>
        </Flex>
        <FormControl>
          <FormLabel my={2}>Where did you hear about us? *</FormLabel>
          <Field
            name="hearing"
            component="select"
            required
            className="customInput"
            onChange={(e) => {
              let option = hearings.find(
                (item) => item.name === e.target.value
              );
              setRequieField(option.requirefield);
            }}
          >
            {hearings.map((hearing) => (
              <option key={hearing.name} value={hearing.name}>
                {hearing.name}
              </option>
            ))}
          </Field>
        </FormControl>
        {requireField && (
          <FormControl>
            <FormLabel my={2}>Please provide details</FormLabel>
            <Field
              name="hearingOption"
              component="input"
              type="text"
              style={{ outline: 'transparent' }}
              className="customInput"
            />
          </FormControl>
        )}
        <FormControl>
          <FormLabel my={2}>Audience / Number of Employees *</FormLabel>
          <Field
            name="size"
            component="select"
            required
            className="customInput"
          >
            {sizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Field>
        </FormControl>
        {type === 'full' && (
          <FormControl>
            <FormLabel my={2}>Job Role</FormLabel>
            <Field name="jobRole" component="select" className="customInput">
              {jobRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Field>
          </FormControl>
        )}

        {type === 'full' && (
          <FormControl>
            <FormLabel my={2}>Country *</FormLabel>
            <Field
              name="country"
              component="select"
              required
              className="customInput"
            >
              {countries.map((country, index) => (
                <option key={index} value={country.countryNameEn}>
                  {country.countryNameEn}
                </option>
              ))}
            </Field>
          </FormControl>
        )}

        {type === 'full' && (
          <FormControl>
            <Flex justifyItems="center" alignContent="center" my={2}>
              <FormLabel my={2}>
                Which product you are most interested in?
              </FormLabel>
              <Tooltip
                label="Go to the footer on this page for links to view all product
                details in full"
              >
                <Box
                  my={2}
                  width={5}
                  height={5}
                  borderRadius={10}
                  alignItems="center"
                  backgroundColor="gray.300"
                >
                  <Text
                    cursor="pointer"
                    fontSize={12}
                    textAlign="center"
                    fontWeight="bold"
                  >
                    ?
                  </Text>
                </Box>
              </Tooltip>
            </Flex>
            <Field
              name="productRequired"
              component="select"
              className="customInput"
            >
              {products.map((product, index) => (
                <option key={index} value={product}>
                  {product}
                </option>
              ))}
            </Field>
          </FormControl>
        )}

        {type === 'full' && (
          <FormControl my={2}>
            <FormLabel>Message *</FormLabel>
            <Field
              name="message"
              component="textarea"
              style={{ outline: 'transparent', minHeight: '150px' }}
              required
              className="customInput"
            />
          </FormControl>
        )}

        <FormLabel>
          <Field
            name="receiveNewsletters"
            component="input"
            type="checkbox"
            normalize={(v) => {
              return !!v ? true : false;
            }}
          />{' '}
          Receive occasional updates from us including new features, articles
          and communications. You can opt out at any time.
        </FormLabel>

        <button className="customButton">Send</button>
      </form>

      {(submitting || sending) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.8)',
          }}
        >
          {/* {submitting && (
            <div>
              <Loader />
            </div>
          )} */}
          {!submitting && (
            <div
              className="alert alert-success"
              style={{ margin: '2rem' }}
              role="alert"
            >
              Thank you for contacting us, we will get back in touch shortly!
            </div>
          )}
        </div>
      )}
    </Flex>
  );
};

const selector = formValueSelector('contactForm');

const mapDispatchToProps = (dispatch) => ({
  resetForm: () => dispatch(reset('contactForm')),
});

ContactForm = connect((state) => ({
  ...selector(state, 'type'),
  mapDispatchToProps,
}))(ContactForm);

export default reduxForm({
  form: 'contactForm',
  destroyOnUnmount: false,
  initialValues: {
    firstName: '',
    lastName: '',
    email: '',
    prefix: 'United Kingdom +44',
    phone: '',
    company: '',
    jobRole: 'Partnership Manager',
    country: 'United Kingdom',
    type: 'Book a demo',
    productRequired: 'Web Curation',
    hearing: 'Recommendation / Referral',
    hearingOption: '',
    message: '',
    size: 'Less than 1,000',
    receiveNewsletters: false,
  },
})(ContactForm);
